import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import '../../../modules/datepicker/react-datepicker.css';
import DatePickerShell from './DatePickerShell';

export interface DateRangePickerProps {
    selected?: Date | Date[] | false;
    setDate: Dispatch<SetStateAction<string>>;
    setDateEnd: Dispatch<SetStateAction<string>>;
    onChange?: (dates: [Date | null, Date | null]) => void;
    disabled?: boolean;
    width?: string;
    mobileWidth?: string;
    className?: string;
}

const resolveInitialRange = (
    selected: Date | Date[] | false | undefined,
): [Date, Date] => {
    if (Array.isArray(selected)) {
        const start = selected[0] ? new Date(selected[0]) : new Date();
        const end = selected[1] ? new Date(selected[1]) : new Date();
        return [start, end];
    }
    if (selected instanceof Date) return [selected, selected];
    const today = new Date();
    return [today, today];
};

const formatRange = (startDate: Date | null, endDate: Date | null): string => {
    if (!startDate) return '';
    const startStr = format(startDate, 'dd/MM/yyyy');
    if (!endDate) return `${startStr} - `;
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
    const sameDay = sameMonth && startDate.getDate() === endDate.getDate();
    if (sameDay) return startStr;
    if (sameMonth) return `${startStr} - ${format(endDate, 'dd')}`;
    if (sameYear) return `${startStr} - ${format(endDate, 'dd/MM')}`;
    return `${startStr} - ${format(endDate, 'dd/MM/yyyy')}`;
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    selected,
    setDate,
    setDateEnd,
    onChange,
    disabled,
    width,
    mobileWidth,
    className,
}) => {
    const [initialStart, initialEnd] = resolveInitialRange(selected);
    const [startDate, setStartDate] = useState<Date | null>(initialStart);
    const [endDate, setEndDate] = useState<Date | null>(initialEnd);

    const handleChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        onChange?.(dates);
    };

    useEffect(() => {
        if (startDate) setDate(format(startDate, 'yyyyMMdd'));
        if (endDate) setDateEnd(format(endDate, 'yyyyMMdd'));
    }, [startDate, endDate, setDate, setDateEnd]);

    return (
        <DatePickerShell
            className={className}
            width={width}
            mobileWidth={mobileWidth}
        >
            <ReactDatePicker
                className='datePicker'
                selected={startDate ?? undefined}
                onChange={handleChange}
                startDate={startDate ?? undefined}
                endDate={endDate ?? undefined}
                selectsRange
                shouldCloseOnSelect={false}
                dateFormat='dd/MM/yyyy'
                locale={he}
                disabled={disabled}
                popperProps={{ strategy: 'fixed' }}
                popperPlacement='bottom-end'
                value={formatRange(startDate, endDate)}
            />
        </DatePickerShell>
    );
};

export default DateRangePicker;
